// ============================================================
// MASH Machine-Evidence — HONESTY ENGINE (pure, host-agnostic, React-free)
// ============================================================
// The honesty rules ARE the design (MASH-LIVE-IMPLEMENTATION.md §3;
// MASH_CARDS_IMPLEMENTATION_HANDOFF.md §3/§4). This module holds ALL the
// load-bearing logic as pure functions so it is exhaustively unit-testable
// WITHOUT rendering React (no axios/Redux import chain). The presentational
// atoms in ../ consume these resolutions; they add NO logic of their own.
//
// LOAD-BEARING RULES enforced here:
//  - Coverage: `cold_armed` and `pending` BOTH render a DASH with DIFFERENT
//    captions; NEITHER is EVER a 0. Never fabricate a number. An unrecognized
//    coverage value renders neutral, never a confident KPI.
//  - Freshness: PROVENANCE INVARIANT (historical_backfill => never LIVE, clamp);
//    the two-clock backfill split; any LIVE/STALE without last_sample_at is
//    forced DARK.
//  - Verdict: unknown/stale are the DIGNIFIED default (slate), never error red.
//  - Exception banner: derive renderer-side (DARK feed OR non-zero safety/alarm
//    figure); camera_inventory is a card-level WATCH, never a global promotion.
//  - Identity invariant: the machine GUID must NEVER leak into a figure /
//    signal_key / verdict / rendered label.

import type {
  Breakdown,
  CoverageBearing,
  EvidenceTab,
  Figure,
  FigureWindow,
  Freshness,
  FreshnessState,
  MachineCardArtifactData,
  SourceGrain,
  SourceRef,
  Verdict,
  VerdictStatus,
  WindowKey,
} from "../../../chatBot/models/machineEvidenceModels";

// ============================================================
// Coverage -> render state
// ============================================================

export type CoverageRenderKind =
  | "value" // a real number/string is shown
  | "live_zero" // armed & quiet real 0 (green dot)
  | "cold_armed" // dash + "armed, not yet captured"
  | "pending" // dash + "not wired yet"
  | "absent" // "not available"
  | "neutral"; // unknown/malformed -> disclaimed, never a confident KPI

export interface CoverageResolution {
  kind: CoverageRenderKind;
  /** true only for `value` / `live_zero` — a real number/string may render. */
  showsValue: boolean;
  /** the value to display (never fabricated). null for every placeholder kind. */
  value: number | string | null;
  /** stable i18n key for the placeholder caption (null for value/live_zero). */
  captionKey: string | null;
  /** full default English caption incl. any appended payload reason. Kept for the
   *  pure-engine consumers / unit tests / fallback (SP-6 renders via `captionKey`). */
  caption: string | null;
  /** reason-LESS English base caption — the render layer uses it as the i18n
   *  `t()` defaultValue so a (never-shipped-missing) key can't double the reason. */
  captionBase: string | null;
  /** payload-provided reason (e.g. `breakdown.pending_reason`), appended AFTER the
   *  translated base and rendered VERBATIM — it is bot data, never translated. */
  captionReason: string | null;
}

const COVERAGE_CAPTIONS: Record<
  Exclude<CoverageRenderKind, "value" | "live_zero">,
  { key: string; text: string }
> = {
  cold_armed: { key: "coverage.coldArmed", text: "armed, not yet captured" },
  pending: { key: "coverage.pending", text: "not wired yet" },
  absent: { key: "coverage.absent", text: "not available" },
  neutral: { key: "coverage.neutral", text: "unavailable" },
};

const placeholder = (
  kind: Exclude<CoverageRenderKind, "value" | "live_zero">,
  reason?: string | null
): CoverageResolution => {
  const base = COVERAGE_CAPTIONS[kind];
  const text = reason ? `${base.text} (${reason})` : base.text;
  return {
    kind,
    showsValue: false,
    value: null,
    captionKey: base.key,
    caption: text,
    captionBase: base.text,
    captionReason: reason ?? null,
  };
};

/**
 * Resolve the render state of a single figure slot (or a per-window slice — see
 * the SP-1b seam). The load-bearing distinctions: cold_armed vs pending BOTH
 * dash with DIFFERENT captions; live_zero is the ONLY legitimate 0; unknown
 * coverage => neutral, never a confident KPI. A wholly-absent figure (undefined)
 * or a value gated on a pending breakdown probe => pending.
 */
export const resolveCoverage = (
  input: CoverageBearing | null | undefined
): CoverageResolution => {
  if (!input) return placeholder("pending");

  const bd = input.breakdown;
  const bdPending = bd?.pending === true;
  const hasValue = input.value !== null && input.value !== undefined;

  switch (input.coverage) {
    case "ok":
      if (hasValue) {
        return {
          kind: "value",
          showsValue: true,
          value: input.value,
          captionKey: null,
          caption: null,
          captionBase: null,
          captionReason: null,
        };
      }
      // "ok" with no value is malformed — never fabricate. If a pending probe
      // gates it, say so; else disclaim neutral.
      return bdPending ? placeholder("pending", bd?.pending_reason) : placeholder("neutral");

    case "live_zero":
      // The armed-and-quiet real zero. Numeric value only; anything else is 0.
      return {
        kind: "live_zero",
        showsValue: true,
        value: typeof input.value === "number" ? input.value : 0,
        captionKey: null,
        caption: null,
        captionBase: null,
        captionReason: null,
      };

    case "cold_armed":
      return placeholder("cold_armed");

    case "absent":
      return placeholder("absent");

    default:
      // Unrecognized coverage value (outside the closed set) — neutral, never
      // a confident KPI. A pending breakdown still surfaces as pending.
      return bdPending ? placeholder("pending", bd?.pending_reason) : placeholder("neutral");
  }
};

// ============================================================
// SP-1b — multi-window figure selection (the 5-pill period row)
// ============================================================
// The pills are CARD-LEVEL: selecting a window re-reads every WINDOWED figure's
// matching `windows[]` entry (value/count/coverage/source_ref). A SNAPSHOT figure
// (no `windows[]`) IGNORES the selector. Switching is PURELY client-side over the
// already-delivered payload (no refetch). LOAD-BEARING honesty rule: a window with
// `coverage:"absent"` renders "not available" — NEVER a value scaled from another
// window (banned). Freshness stays CARD-LEVEL (§7A #1) — window switching does NOT
// touch the freshness resolution.

/** The five window keys in their fixed emission/render order (the pill-row order).
 *  Lives here (not the type-only models file) alongside the other contract-derived
 *  runtime constants. */
export const WINDOW_KEYS: readonly WindowKey[] = [
  "last_hour",
  "this_shift",
  "today",
  "this_week",
  "all_time",
];

/** The default PILL-ROW window key (`today` — the natural operational default,
 *  matching the single-day granularity of the canonical FEEDER example).
 *
 *  ME-CARDS-R1 A2: this is NO LONGER the card's initial selection. Initializing
 *  the viewer to a hardcoded enum key rendered "not available" over real data
 *  whenever the turn resolved to a window outside the closed 5-key enum. The
 *  card now initializes via `initialWindowSelection(resolveTurnWindow(card))`.
 *  The constant stays as the enum's stated operational default for any consumer
 *  that needs a bare key (and to keep the pill order/semantics documented). */
export const DEFAULT_WINDOW_KEY: WindowKey = "today";

/** A figure carries per-window rollups iff it has a non-empty `windows[]` array.
 *  Everything else is a SNAPSHOT (latest-wins) figure that ignores the pill row. */
export const isWindowedFigure = (figure: Figure | null | undefined): boolean =>
  !!figure && Array.isArray(figure.windows) && figure.windows.length > 0;

/** True when ANY figure across the card carries `windows[]`. When FALSE (every
 *  figure is a snapshot) the pill row is HIDDEN ENTIRELY (not rendered disabled). */
export const cardHasWindowedFigures = (
  data: MachineCardArtifactData | null | undefined
): boolean => {
  if (!data) return false;
  for (const tab of data.tabs ?? []) {
    for (const fig of tab.figures ?? []) {
      if (isWindowedFigure(fig)) return true;
    }
  }
  return false;
};

/** Select a windowed figure's entry for a given window key (or null if the
 *  figure is a snapshot / the key is absent). */
export const selectFigureWindow = (
  figure: Figure | null | undefined,
  key: WindowKey
): FigureWindow | null => {
  if (!figure || !Array.isArray(figure.windows)) return null;
  return figure.windows.find((w) => w.window_key === key) ?? null;
};

/**
 * Project a figure onto the selected window for rendering.
 *  - SNAPSHOT figure (no `windows[]`) => returned UNCHANGED (ignores the selector,
 *    keeps its single current-state value + breakdown).
 *  - WINDOWED figure => a shallow clone with `value`/`coverage`/`n`/`source_ref`
 *    taken from the matching window entry. `denominator` + `breakdown` are dropped
 *    (the pinned `windows[]` entry carries only a scalar + count, not a num/den
 *    pair or a per-window breakdown); `threshold`/`unit`/`label` are window-
 *    agnostic and inherited. A MISSING key (contract guarantees all 5, but be
 *    defensive) => `coverage:"absent"` — NEVER fall back to another window's value
 *    (that would be a scaled/mislabeled number, which is BANNED).
 * Feed the result straight into `resolveCoverage` (so `absent` => "not available",
 * a real `0` with `coverage:"ok"` => `0`).
 */
export const projectFigureToWindow = (figure: Figure, selected: WindowKey): Figure => {
  if (!isWindowedFigure(figure)) return figure;
  const entry = (figure.windows ?? []).find((w) => w.window_key === selected) ?? null;
  if (!entry) {
    return { ...figure, value: null, coverage: "absent", denominator: null, breakdown: null, n: null };
  }
  return {
    ...figure,
    value: entry.value,
    coverage: entry.coverage,
    denominator: null,
    breakdown: null,
    n: entry.count ?? null,
    source_ref: entry.source_ref ?? figure.source_ref ?? null,
  };
};

// ============================================================
// ME-CARDS-R1 A2 — TURN-resolved window selection
// ============================================================
// THE bug this fixes: the pinned `windows[]` enum is CLOSED at 5 keys, but a
// turn can resolve to a window OUTSIDE that enum (the live arm card resolved to
// `"yesterday"`). The figure's PRIMARY fields (`value`/`coverage`/`n`/
// `denominator`/`breakdown`) ARE that turn-resolved read — the exact read the
// verdict headline and the provenance footer were computed on. Projecting
// unconditionally onto a hardcoded enum key therefore rendered "not available"
// OVER real data while the verdict quoted it (arm.PNG).
//
// So the selection is no longer a bare `WindowKey`: it is either the TURN (the
// primary read, the initial state) or one of the 5 pinned enum windows. Nothing
// here relaxes an honesty rule — a user who CLICKS a window whose slice is
// `absent` still gets "not available". Only the DEFAULT changed.

/** Card-level window selection. `turn` = render the figure's own primary
 *  (turn-resolved) read; `window` = project onto one of the 5 pinned keys. */
export type WindowSelection = { kind: "turn" } | { kind: "window"; key: WindowKey };

/** The card's PRIMARY window descriptor, as emitted. `token` is the raw
 *  `figure.window` string (e.g. `"yesterday"`, `"2026-06-10 → 2026-06-10"`) —
 *  bot data, never translated blind. */
export interface TurnWindow {
  token: string | null;
  start: string | null;
  end: string | null;
}

const WINDOW_KEY_SET: ReadonlySet<string> = new Set<string>(WINDOW_KEYS);

/** True when a raw window token is one of the five PINNED enum keys (so the
 *  pill row can already express it and no extra "turn" pill is needed). */
export const isWindowKey = (token: string | null | undefined): token is WindowKey =>
  !!token && WINDOW_KEY_SET.has(token);

/**
 * Resolve the card's turn window from the FIRST windowed figure that carries
 * each part, falling back across figures to the first non-null value. Snapshot
 * figures are skipped — they ignore window selection entirely, so their own
 * `window` must never define the card's turn window.
 */
export const resolveTurnWindow = (
  data: MachineCardArtifactData | null | undefined
): TurnWindow => {
  const out: TurnWindow = { token: null, start: null, end: null };
  if (!data) return out;
  for (const tab of data.tabs ?? []) {
    for (const fig of tab.figures ?? []) {
      if (!isWindowedFigure(fig)) continue;
      if (out.token === null && fig.window && fig.window.trim().length > 0) out.token = fig.window;
      if (out.start === null && fig.window_start) out.start = fig.window_start;
      if (out.end === null && fig.window_end) out.end = fig.window_end;
      if (out.token !== null && out.start !== null && out.end !== null) return out;
    }
  }
  return out;
};

/** The card's INITIAL selection: the turn-resolved primary read, unless the turn
 *  window IS one of the 5 pinned keys — then select that pill (which renders the
 *  primary fields anyway, via the equal-token exception in
 *  `projectFigureToSelection`). */
export const initialWindowSelection = (turn: TurnWindow): WindowSelection =>
  isWindowKey(turn.token) ? { kind: "window", key: turn.token } : { kind: "turn" };

/**
 * Project a figure onto the current SELECTION (the A2 replacement for calling
 * `projectFigureToWindow` unconditionally).
 *  - `turn` => the figure UNCHANGED: primary `value`/`coverage`/`n` PLUS
 *    `denominator` + `breakdown`, which the pinned `windows[]` slices do not
 *    carry by contract.
 *  - `window` whose key EQUALS the figure's own primary window token => also
 *    unchanged: the primary read is strictly richer than its own slice, so it
 *    must win for its own window (never degrade a figure to a lossy copy of
 *    itself).
 *  - any other `window` => the existing `projectFigureToWindow` (absent slices
 *    stay "not available"; nothing is ever scaled from another window).
 */
export const projectFigureToSelection = (
  figure: Figure,
  selection: WindowSelection
): Figure => {
  if (selection.kind === "turn") return figure;
  if (figure.window && figure.window === selection.key) return figure;
  return projectFigureToWindow(figure, selection.key);
};

// ============================================================
// Threshold band (ME-CARDS-R1 A5 — the arm-model gauge)
// ============================================================
// The residual figure is the ONLY figure carrying a `threshold`. The design
// renders it as a horizontal tolerance band: `low | target | high`, an in-band
// fill, and a marker at the figure's value. Pure geometry lives here so the atom
// stays a dumb renderer.
//
// HONESTY: a null / non-numeric value, or a value outside the drawn range, gets
// NO marker and NO inside/outside verdict — we never imply a position we cannot
// honestly draw. The band itself still renders (it IS payload data).

export interface ThresholdBandResolution {
  low: number;
  high: number;
  target: number | null;
  /** drawn range (band + 25% padding each side). */
  min: number;
  max: number;
  /** 0-100 positions within the drawn range. */
  bandStartPct: number;
  bandEndPct: number;
  targetPct: number | null;
  /** null when no marker may be drawn (absent value / outside the drawn range). */
  markerPct: number | null;
  /** stated ONLY when a marker was placed — never a claim without a position. */
  state: "inside" | "outside" | null;
  /** |low| === high => the design's "±X" phrasing; null when asymmetric. */
  symmetricTolerance: number | null;
}

const pct = (v: number, min: number, max: number): number => ((v - min) / (max - min)) * 100;

/** Resolve a figure's tolerance band for rendering. Returns null when the figure
 *  carries no usable numeric `low`/`high` pair (no band, no invented scale). */
export const resolveThresholdBand = (
  figure: Figure | null | undefined
): ThresholdBandResolution | null => {
  const t = figure?.threshold;
  if (!t) return null;
  const { low, high } = t;
  if (typeof low !== "number" || typeof high !== "number") return null;
  if (!Number.isFinite(low) || !Number.isFinite(high) || high <= low) return null;

  const span = high - low;
  const pad = span * 0.25;
  const min = low - pad;
  const max = high + pad;

  const target = typeof t.target === "number" && Number.isFinite(t.target) ? t.target : null;
  const value = typeof figure!.value === "number" && Number.isFinite(figure!.value) ? figure!.value : null;
  const drawable = value !== null && value >= min && value <= max;

  return {
    low,
    high,
    target,
    min,
    max,
    bandStartPct: pct(low, min, max),
    bandEndPct: pct(high, min, max),
    targetPct: target !== null && target >= min && target <= max ? pct(target, min, max) : null,
    markerPct: drawable ? pct(value as number, min, max) : null,
    state: drawable ? ((value as number) >= low && (value as number) <= high ? "inside" : "outside") : null,
    symmetricTolerance: Math.abs(low) === high ? high : null,
  };
};

// ============================================================
// Breakdown -> grouped rows (cap 8, honor truncated + pending)
// ============================================================

export const BREAKDOWN_ROW_CAP = 8;

export interface BreakdownResolution {
  pending: boolean;
  pendingReason: string | null;
  groupLabel: string | null;
  rows: Breakdown["rows"];
  /** true when more rows exist than are shown (from truncated OR the cap). */
  isTruncated: boolean;
  shown: number;
  total: number;
}

/** Resolve a figure breakdown: pending => no rows (never fake); otherwise cap
 *  rows at 8 and reconcile the truncated count. */
export const resolveBreakdown = (
  breakdown: Breakdown | null | undefined
): BreakdownResolution | null => {
  if (!breakdown) return null;
  if (breakdown.pending) {
    return {
      pending: true,
      pendingReason: breakdown.pending_reason ?? null,
      groupLabel: breakdown.group_label ?? null,
      rows: [],
      isTruncated: false,
      shown: 0,
      total: breakdown.truncated?.total ?? 0,
    };
  }
  const allRows = Array.isArray(breakdown.rows) ? breakdown.rows : [];
  const rows = allRows.slice(0, BREAKDOWN_ROW_CAP);
  const total = breakdown.truncated?.total ?? allRows.length;
  const shown = rows.length;
  return {
    pending: false,
    pendingReason: null,
    groupLabel: breakdown.group_label ?? null,
    rows,
    isTruncated: shown < total,
    shown,
    total,
  };
};

// ============================================================
// Freshness -> LIVE/STALE/DARK + two clocks
// ============================================================

/** Live-stream demo thresholds (MASH prototype §4: LIVE <=8s, STALE <=20s,
 *  else DARK). Used by `deriveFreshnessState` ONLY as a fallback when the bot
 *  did not emit an explicit `state`. Snapshot cards this milestone TRUST the
 *  bot's emitted state (it is the receipt-clock result computed server-side) —
 *  recomputing a legitimately-STALE 20-day backfill with live thresholds would
 *  wrongly force it DARK. */
export interface FreshnessThresholds {
  liveMaxSeconds: number;
  staleMaxSeconds: number;
}
export const DEFAULT_FRESHNESS_THRESHOLDS: FreshnessThresholds = {
  liveMaxSeconds: 8,
  staleMaxSeconds: 20,
};

/** Pure state machine: map a receipt-clock age to LIVE/STALE/DARK. A null age
 *  is a read fault => DARK (never a fabricated LIVE). */
export const deriveFreshnessState = (
  ageSeconds: number | null | undefined,
  thresholds: FreshnessThresholds = DEFAULT_FRESHNESS_THRESHOLDS
): FreshnessState => {
  if (ageSeconds === null || ageSeconds === undefined || !Number.isFinite(ageSeconds)) {
    return "DARK";
  }
  if (ageSeconds <= thresholds.liveMaxSeconds) return "LIVE";
  if (ageSeconds <= thresholds.staleMaxSeconds) return "STALE";
  return "DARK";
};

export interface TwoClock {
  /** the older EVENT clock (raw ISO). */
  eventTime: string;
  /** the RECEIPT clock (raw ISO). */
  receivedAt: string;
  /** DD.MM.YYYY (Slovak-first locale — design brief §1). */
  eventTimeFormatted: string;
  receivedAtFormatted: string;
}

export interface FreshnessResolution {
  /** effective state AFTER the invariants (clamp + forced-dark). */
  state: FreshnessState;
  /** true when the PROVENANCE INVARIANT clamped a claimed LIVE off backfill. */
  clampedFromLive: boolean;
  /** true when a LIVE/STALE claim was forced DARK by a missing last_sample_at. */
  forcedDark: boolean;
  /** true when provenance === "historical_backfill". */
  isBackfill: boolean;
  /** non-null ONLY on backfill with both clocks present — render the split. */
  twoClock: TwoClock | null;
  /** DARK => dim content. */
  dim: boolean;
  /** DARK => freeze counts. */
  freeze: boolean;
  ageSeconds: number | null;
  /** the raw freshness block (null when none was supplied). */
  raw: Freshness | null;
}

const VALID_STATES: ReadonlySet<string> = new Set<FreshnessState>(["LIVE", "STALE", "DARK"]);

/**
 * Resolve card freshness. Enforces the two hard invariants on the bot-emitted
 * `state`:
 *   1. PROVENANCE INVARIANT — historical_backfill => never LIVE (clamp -> STALE).
 *   2. FORCED DARK — a LIVE/STALE claim with no `last_sample_at` is forced DARK.
 * When the emitted `state` is absent/unrecognized it falls back to the receipt-
 * clock state machine (`deriveFreshnessState`); with no data at all => DARK.
 */
export const resolveFreshness = (
  freshness: Freshness | null | undefined,
  thresholds: FreshnessThresholds = DEFAULT_FRESHNESS_THRESHOLDS
): FreshnessResolution => {
  if (!freshness) {
    return {
      state: "DARK",
      clampedFromLive: false,
      forcedDark: false,
      isBackfill: false,
      twoClock: null,
      dim: true,
      freeze: true,
      ageSeconds: null,
      raw: null,
    };
  }

  const isBackfill = freshness.provenance === "historical_backfill";
  const hasSample = !!freshness.last_sample_at;

  // Baseline: trust the emitted state; fall back to the receipt-clock machine
  // for a missing/unrecognized state.
  let state: FreshnessState = VALID_STATES.has(freshness.state)
    ? freshness.state
    : deriveFreshnessState(freshness.age_seconds, thresholds);

  // 1. PROVENANCE INVARIANT — a replayed feed is never LIVE.
  let clampedFromLive = false;
  if (isBackfill && state === "LIVE") {
    state = "STALE";
    clampedFromLive = true;
  }

  // 2. FORCED DARK — a LIVE/STALE claim without a receipt clock is a fabricated
  // freshness; force DARK.
  let forcedDark = false;
  if ((state === "LIVE" || state === "STALE") && !hasSample) {
    state = "DARK";
    forcedDark = true;
  }

  // Two-clock split: only on backfill with BOTH clocks present.
  let twoClock: TwoClock | null = null;
  if (isBackfill && freshness.event_time && freshness.last_sample_at) {
    twoClock = {
      eventTime: freshness.event_time,
      receivedAt: freshness.last_sample_at,
      eventTimeFormatted: formatIsoDateDDMMYYYY(freshness.event_time),
      receivedAtFormatted: formatIsoDateDDMMYYYY(freshness.last_sample_at),
    };
  }

  const dark = state === "DARK";
  return {
    state,
    clampedFromLive,
    forcedDark,
    isBackfill,
    twoClock,
    dim: dark,
    freeze: dark,
    ageSeconds: freshness.age_seconds ?? null,
    raw: freshness,
  };
};

/** Default-English backfill phrase built from a resolution's two-clock split.
 *  NEVER collapse to a bare "20 h ago". Returns null when there is no split.
 *  SP-6 re-expresses this via i18n interpolation of the TwoClock parts. */
export const formatBackfillSplit = (resolution: FreshnessResolution): string | null => {
  if (!resolution.twoClock) return null;
  const { eventTimeFormatted, receivedAtFormatted } = resolution.twoClock;
  return `historical backfill — events from ${eventTimeFormatted}, received ${receivedAtFormatted}`;
};

/** Extract the wall-clock calendar date from an ISO string and format it
 *  DD.MM.YYYY (Slovak-first). Deterministic + timezone-independent: it reads the
 *  date component AS WRITTEN in the ISO string (respecting its own offset), which
 *  is the date an operator reads. Returns the raw input if it is not ISO-shaped. */
export const formatIsoDateDDMMYYYY = (iso: string | null | undefined): string => {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const [, y, mo, d] = m;
  return `${d}.${mo}.${y}`;
};

/** Same contract as `formatIsoDateDDMMYYYY` but keeping the 24h wall-clock time
 *  AS WRITTEN in the ISO string ("21.08.2026 14:18") — the time an operator
 *  reads off the machine. Falls back to the date alone when the string carries
 *  no time, and to the raw input when it is not ISO-shaped (ME-CARDS-R1 A5:
 *  the STALE freshness detail needs the sample TIME, not just its date). */
export const formatIsoDateTimeDDMMYYYY = (iso: string | null | undefined): string => {
  const date = formatIsoDateDDMMYYYY(iso);
  if (!iso || !date || date === iso) return date;
  const t = /^\d{4}-\d{2}-\d{2}[T ](\d{2}):(\d{2})/.exec(iso);
  return t ? `${date} ${t[1]}:${t[2]}` : date;
};

/** Coarse age bucket for the freshness detail line. The RENDER layer maps the
 *  unit to an i18n label — this stays pure + language-free. A null/negative/
 *  non-finite age has no honest bucket => null (the caller shows no age). */
export type AgeUnit = "seconds" | "minutes" | "hours" | "days";
export interface AgeParts {
  unit: AgeUnit;
  value: number;
}
export const resolveAgeParts = (ageSeconds: number | null | undefined): AgeParts | null => {
  if (ageSeconds === null || ageSeconds === undefined) return null;
  if (!Number.isFinite(ageSeconds) || ageSeconds < 0) return null;
  if (ageSeconds < 90) return { unit: "seconds", value: Math.round(ageSeconds) };
  if (ageSeconds < 5400) return { unit: "minutes", value: Math.round(ageSeconds / 60) };
  if (ageSeconds < 172800) return { unit: "hours", value: Math.round(ageSeconds / 3600) };
  return { unit: "days", value: Math.round(ageSeconds / 86400) };
};

// ============================================================
// Verdict -> status block
// ============================================================

export type VerdictTone = "positive" | "warning" | "critical" | "neutral";

export interface VerdictResolution {
  /** normalized status; an unrecognized value normalizes to "unknown". */
  status: VerdictStatus;
  /** slate(neutral) is the dignified default; NEVER an error tone. */
  tone: VerdictTone;
  headline: string;
  /** true when `headline` is the client "Status unknown" FALLBACK (no payload
   *  headline) — the render layer translates that; a payload headline renders as-is. */
  headlineIsFallback: boolean;
  /** "Judged against <basis>" when a basis is present; else null. English
   *  fallback — the render layer rebuilds it from `basis` via i18n. */
  basisLine: string | null;
  /** raw payload basis (never translated) — the render layer interpolates it into
   *  the translated "judged against {basis}" prefix. */
  basis: string | null;
  /** optional grounded elaboration (always null this milestone). */
  detail: string | null;
}

const VERDICT_TONES: Record<VerdictStatus, VerdictTone> = {
  on_target: "positive",
  elevated: "warning",
  fault: "critical",
  stale: "neutral",
  cold: "neutral",
  unknown: "neutral",
};

const KNOWN_VERDICT_STATUSES: ReadonlySet<string> = new Set<VerdictStatus>([
  "on_target",
  "elevated",
  "fault",
  "stale",
  "cold",
  "unknown",
]);

/** Resolve a verdict object into a styled status block. `unknown`/`stale` (and
 *  any unrecognized status) resolve to the slate NEUTRAL tone — the dignified
 *  default, NOT error red. The card NEVER fabricates on_target/fault. */
export const resolveVerdict = (verdict: Verdict | null | undefined): VerdictResolution => {
  const status: VerdictStatus =
    verdict && KNOWN_VERDICT_STATUSES.has(verdict.status) ? verdict.status : "unknown";
  const hasHeadline = !!(verdict && verdict.headline && verdict.headline.trim().length > 0);
  const headline = hasHeadline ? verdict!.headline : "Status unknown";
  const basis = verdict?.basis && verdict.basis.trim().length > 0 ? verdict.basis : null;
  return {
    status,
    tone: VERDICT_TONES[status],
    headline,
    headlineIsFallback: !hasHeadline,
    basisLine: basis ? `Judged against ${basis}` : null,
    basis,
    detail: verdict?.detail ?? null,
  };
};

// ============================================================
// Source-grain label
// ============================================================

const GRAIN_LABELS: Record<SourceGrain, string> = {
  event: "raw event aggregate",
  numeric: "numeric read",
  live_snapshot: "live snapshot",
  rollup_cagg: "as of last completed bucket",
};

/** Human label for a source grain; `rollup_cagg` => "as of last completed
 *  bucket". Unknown grain passes through verbatim (never a confident claim). */
export const sourceGrainLabel = (grain: SourceGrain | string | null | undefined): string => {
  if (!grain) return "";
  return (GRAIN_LABELS as Record<string, string>)[grain] ?? String(grain);
};

// ============================================================
// Identity invariant — GUID guard
// ============================================================

const GUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/** True if the string looks like (or contains) a GUID. */
export const looksLikeGuid = (value: string | null | undefined): boolean =>
  !!value && GUID_RE.test(value);

export interface GuidGuardResult {
  /** the safe string to render (blanked when a leak was detected). */
  safe: string;
  /** true when the GUID (or any GUID-shaped token) was present and stripped. */
  leaked: boolean;
}

/**
 * IDENTITY INVARIANT guard. The machine GUID must NEVER surface inside a figure,
 * a signal_key, a verdict, or a rendered label — only in the identity header.
 * This is the fail-safe: if a GUID (the card's own, or ANY GUID-shaped token)
 * reaches a render slot it is stripped, and in dev we shout. Returns a blanked
 * string so the caller renders nothing rather than a raw GUID.
 */
export const guardAgainstGuid = (
  value: string | null | undefined,
  machineGuid?: string | null
): GuidGuardResult => {
  if (!value) return { safe: "", leaked: false };
  const hitsOwnGuid = !!machineGuid && value.toLowerCase().includes(machineGuid.toLowerCase());
  const hitsAnyGuid = looksLikeGuid(value);
  if (hitsOwnGuid || hitsAnyGuid) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.error(
        "[machineEvidence] IDENTITY INVARIANT violated — a GUID reached a render slot and was stripped:",
        value
      );
    }
    return { safe: "", leaked: true };
  }
  return { safe: value, leaked: false };
};

// ============================================================
// Provenance footer — join figures -> sources
// ============================================================

export interface FooterEntry {
  /** the CITED SOURCE's id — the row identity (ME-CARDS-R1 A3). One row per
   *  source; `figureId`/`figureLabel` below are the FIRST citing figure and are
   *  display-unused. */
  sourceId: string;
  figureId: string;
  figureLabel: string;
  /** store-mapped key ONLY (GUID-guarded — never a GUID). */
  signalKey: string;
  readSource: string | null;
  window: string | null;
  windowStart: string | null;
  windowEnd: string | null;
  count: number | null;
  sourceGrain: SourceGrain | null;
  /** rollup_cagg => "as of last completed bucket". */
  grainLabel: string;
}

/** Build the provenance footer: join each figure's `source_ref` to a
 *  `sources[]` entry. This REPLACES the inline `[[tel:]]` chips — do not render
 *  those. `signal_key` is GUID-guarded (identity invariant). Figures without a
 *  resolvable source_ref are skipped (no fabricated provenance).
 *
 *  ME-CARDS-R1 A3: dedupe is per CITED SOURCE (`src.id`), NOT per figure-source
 *  pair. The rendered row carries only source fields (signal_key / read_source /
 *  window / count / grain) and never the figure label, so two figures citing one
 *  source produced two IDENTICAL rows (arm.PNG). The first citing figure supplies
 *  the display-unused `figureId`/`figureLabel`. Two DISTINCT sources that happen
 *  to share a `signal_key` still render two rows — the identity is the source id. */
export const buildProvenanceFooter = (data: MachineCardArtifactData): FooterEntry[] => {
  const guid = data.machine?.machine_id_full || data.machine?.machine || null;
  const sourceById = new Map<string, SourceRef>();
  for (const s of data.sources ?? []) sourceById.set(s.id, s);

  const entries: FooterEntry[] = [];
  const seen = new Set<string>();
  for (const tab of data.tabs ?? []) {
    for (const fig of tab.figures ?? []) {
      if (!fig.source_ref) continue;
      const src = sourceById.get(fig.source_ref);
      if (!src) continue;
      if (seen.has(src.id)) continue;
      seen.add(src.id);
      const guard = guardAgainstGuid(src.signal_key, guid);
      entries.push({
        sourceId: src.id,
        figureId: fig.id,
        figureLabel: fig.label,
        signalKey: guard.safe,
        readSource: src.read_source,
        window: src.window,
        windowStart: src.window_start,
        windowEnd: src.window_end,
        count: src.count,
        sourceGrain: src.source_grain,
        grainLabel: sourceGrainLabel(src.source_grain),
      });
    }
  }
  return entries;
};

// ============================================================
// Per-figure freshness fallback (GAP-7)
// ============================================================

/** Per-tile freshness: `figure.freshness` is always null this milestone, so the
 *  per-tile dot falls back to the CARD-level freshness. */
export const resolveFigureFreshness = (
  figure: Figure | null | undefined,
  cardFreshness: Freshness | null | undefined
): FreshnessResolution => resolveFreshness(figure?.freshness ?? cardFreshness);

// ============================================================
// Exception banner derivation (GAP-8)
// ============================================================

/** Hard safety/alarm signal keys — a NON-ZERO figure on any of these promotes
 *  the global exception banner. (0-count signals are OMITTED by the bot this
 *  milestone — GAP-5 — so a present alarm figure is inherently non-zero.) */
export const HARD_ALARM_SIGNAL_KEYS: ReadonlySet<string> = new Set<string>([
  "safety_trip",
  "motor_fault",
  "s7_link_drop",
  "vision_crash",
]);

export const CAMERA_INVENTORY_SIGNAL_KEY = "camera_inventory";

export type BannerLevel = "none" | "watch" | "critical";
export type BannerReason = "dark_feed" | "safety_alarm" | null;

export interface ExceptionBannerResolution {
  /** true when the banner is promoted ABOVE the hero (GLOBAL). */
  promote: boolean;
  level: BannerLevel;
  reason: BannerReason;
}

const figureValueNonZero = (fig: Figure): boolean =>
  typeof fig.value === "number" ? fig.value > 0 : fig.value !== null && fig.value !== undefined;

/**
 * Derive the GLOBAL exception banner renderer-side (the payload has no promote
 * flag — GAP-8). Promote when the feed is DARK, OR a non-zero hard safety/alarm
 * figure is present. `camera_inventory` is EXPLICITLY NOT a global promotion —
 * it is a card-level WATCH (see deriveCameraWatch).
 */
export const deriveExceptionBanner = (
  data: MachineCardArtifactData
): ExceptionBannerResolution => {
  const freshness = resolveFreshness(data.freshness);
  if (freshness.state === "DARK") {
    return { promote: true, level: "critical", reason: "dark_feed" };
  }

  const sourceById = new Map<string, SourceRef>();
  for (const s of data.sources ?? []) sourceById.set(s.id, s);

  for (const tab of data.tabs ?? []) {
    for (const fig of tab.figures ?? []) {
      const key = fig.source_ref ? sourceById.get(fig.source_ref)?.signal_key : undefined;
      if (key && HARD_ALARM_SIGNAL_KEYS.has(key) && figureValueNonZero(fig)) {
        return { promote: true, level: "critical", reason: "safety_alarm" };
      }
    }
  }

  return { promote: false, level: "none", reason: null };
};

/** Card-level camera WATCH (amber) — the Vision tab ONLY. A camera drop is a
 *  card-level watch, NEVER a global-banner promotion. Returns true when the tab
 *  carries a `camera_inventory` figure. */
export const deriveCameraWatch = (
  tab: EvidenceTab | null | undefined,
  data: MachineCardArtifactData
): boolean => {
  if (!tab) return false;
  const sourceById = new Map<string, SourceRef>();
  for (const s of data.sources ?? []) sourceById.set(s.id, s);
  return (tab.figures ?? []).some((fig) => {
    const key = fig.source_ref ? sourceById.get(fig.source_ref)?.signal_key : undefined;
    return key === CAMERA_INVENTORY_SIGNAL_KEY;
  });
};

// ============================================================
// Small honest helpers
// ============================================================

/** The renderer disclaims a low sample count (contract: e.g. n < 8). */
export const LOW_SAMPLE_THRESHOLD = 8;
export const isLowSample = (n: number | null | undefined): boolean =>
  typeof n === "number" && n > 0 && n < LOW_SAMPLE_THRESHOLD;

/** A structural placeholder tab: no figures + verdict unknown (the bot omits
 *  rather than fabricates — GAP-14). The renderer shows "Pending — not wired
 *  this milestone", never zeros/faults. */
export const isPlaceholderTab = (tab: EvidenceTab | null | undefined): boolean =>
  !!tab && (!tab.figures || tab.figures.length === 0) && tab.verdict?.status === "unknown";

/**
 * Identity header title with GAP-13 fallback: `class_template` null => fall back
 * to `display_name`.
 *
 * ME-CARDS-R1 A1: each candidate now passes the GUID guard. `guardAgainstGuid`
 * deliberately protects only figure/source slots, so a GUID arriving in
 * `class_template` or `display_name` (the live 22.08 payload) rendered VERBATIM
 * as the card's large-type header. A GUID-bearing candidate is SKIPPED (and
 * logged in dev via the guard's existing invariant-violation console.error);
 * when no candidate survives this returns "" and the CALLER renders the
 * translated `card.titleFallback` — never a raw GUID, never a half-identity.
 */
export const resolveIdentityTitle = (data: MachineCardArtifactData): string => {
  const m = data.machine;
  if (!m) return "";
  const ownGuid = m.machine_id_full || m.machine || null;
  for (const candidate of [m.class_template, m.display_name]) {
    if (!candidate || candidate.trim().length === 0) continue;
    const guard = guardAgainstGuid(candidate, ownGuid);
    if (guard.safe) return guard.safe;
  }
  return "";
};
